class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();
		this.loadUpsell();
		this.initPromoCode();
		this.renderDeliveryDate();
		this.renderStockIndicators();
		this.initSizeModal();
		this.setupMutationObserver();
	}

	setupMutationObserver() {
		var self = this;
		var debounceTimer = null;

		this._mutationObserver = new MutationObserver(function(mutations) {
			var meaningful = mutations.some(function(m) {
				if (m.type !== "childList") return false;
				if (m.target && m.target.id === "AlDeliveryDate") return false;
				if (m.target && m.target.classList && m.target.classList.contains("al-stock-indicator")) return false;
				if (m.target && m.target.id === "AlUpsellList") return false;
				if (m.target && m.target.closest && m.target.closest("#AlSizeModal")) return false;
				if (m.target && m.target.closest && m.target.closest("[data-tier-offer]")) return false;
				return true;
			});
			if (!meaningful) return;

			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(function() {
				self.rehydrate();
			}, 200);
		});

		this._mutationObserver.observe(this, {
			childList: true,
			subtree: true
		});
	}

	setHeaderCartIconAccessibility() {
		const cartLinks = document.querySelectorAll("#cart-icon-bubble, #cart-icon-bubble-mobile");
		cartLinks.forEach((cartLink) => {
			cartLink.setAttribute("role", "button");
			cartLink.setAttribute("aria-haspopup", "dialog");

			cartLink.addEventListener("click", (event) => {
				event.preventDefault();
				this.open(cartLink);
			});

			cartLink.addEventListener("keydown", (event) => {
				if (event.code.toUpperCase() === "SPACE") {
					event.preventDefault();
					this.open(cartLink);
				}
			});
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);

		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		if (!this.classList.contains("active"))
		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.add("overflow-hidden");
		} else {
			// Safari fix (Mac + iPhone): .overflow-clip sets position:fixed
			// on body, which visually resets scroll to top. Save the scroll
			// position and offset the body by it so the page doesn't jump.
			// Guard: only lock once — open() also fires while the drawer is
			// already open, and re-saving then would store 0 and jump.
			if (!document.body.classList.contains("overflow-clip")) {
				this._savedScrollY = window.scrollY || window.pageYOffset || 0;
				document.body.style.top = "-" + this._savedScrollY + "px";
				document.body.classList.add("overflow-clip");
			}
		}
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.remove("overflow-hidden");
		} else {
			// Safari fix: restore the scroll position saved in open().
			if (document.body.classList.contains("overflow-clip")) {
				document.body.classList.remove("overflow-clip");
				document.body.style.top = "";
				if (typeof this._savedScrollY === "number") {
					var htmlEl = document.documentElement;
					var prevHtmlBehavior = htmlEl.style.scrollBehavior;
					var prevBodyBehavior = document.body.style.scrollBehavior;
					htmlEl.style.scrollBehavior = "auto";
					document.body.style.scrollBehavior = "auto";
					window.scrollTo(0, this._savedScrollY);
					htmlEl.style.scrollBehavior = prevHtmlBehavior;
					document.body.style.scrollBehavior = prevBodyBehavior;
					this._savedScrollY = undefined;
				}
			}
		}

		setTimeout(() => {
			let notification = this.querySelector(".notification");
			if (notification) {
				const activeItem = this.querySelector(
					'.cart-item[data-product-id="' + this.productId + '"]'
				);
				notification.classList.remove("notification-open");
				if (activeItem) activeItem.classList.remove("active");
			}
		}, 1000);
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		let notification = this.querySelector(".notification");
		if (notification) {
			const activeItem = this.querySelector(
				'.cart-item[data-product-id="' + this.productId + '"]'
			);
			notification.classList.add("notification-open");
			if (activeItem) activeItem.classList.add("active");
		}

		this.rehydrate();

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.open();
		});
	}

	rehydrate() {
		this.loadUpsell();
		this.initPromoCode();
		this.renderDeliveryDate();
		this.renderStockIndicators();
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{ id: "cart-drawer", selector: "#CartDrawer" },
			{ id: "cart-icon-bubble" }
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}

	// ============================================================
	// i18n helpers — locale detection and translation reads
	// ============================================================

	getLocale() {
		// 4-layer fallback for maximum reliability
		try {
			if (typeof window.AL_LOCALE === "string" && window.AL_LOCALE.length >= 2) {
				return window.AL_LOCALE;
			}
		} catch (e) {}
		try {
			if (window.Shopify && window.Shopify.locale) {
				return window.Shopify.locale;
			}
		} catch (e) {}
		try {
			if (document.documentElement && document.documentElement.lang) {
				return document.documentElement.lang;
			}
		} catch (e) {}
		try {
			if (navigator && navigator.language) {
				return navigator.language;
			}
		} catch (e) {}
		return "en";
	}

	t(key, fallback) {
		// Safe translation read with English fallback
		try {
			if (window.AL_STRINGS && window.AL_STRINGS.cart_drawer && window.AL_STRINGS.cart_drawer[key]) {
				return window.AL_STRINGS.cart_drawer[key];
			}
		} catch (e) {}
		return fallback;
	}

	// ============================================================
	// Delivery date — localized via Intl.DateTimeFormat
	// ============================================================

	renderDeliveryDate() {
		const dateEl = this.querySelector("#AlDeliveryDate");
		if (!dateEl) return;

		const now = new Date();
		const vilniusHour = this.getVilniusHour(now);
		let shipOffset = vilniusHour < 14 ? 0 : 1;

		const shipDate = this.addBusinessDays(now, shipOffset);
		const arriveDate = this.addBusinessDays(shipDate, 5);

		const formatted = this.formatDeliveryDate(arriveDate);
		dateEl.textContent = formatted;
	}

	getVilniusHour(date) {
		try {
			const parts = new Intl.DateTimeFormat("en-GB", {
				timeZone: "Europe/Vilnius",
				hour: "numeric",
				hour12: false
			}).formatToParts(date);
			const hourPart = parts.find((p) => p.type === "hour");
			return hourPart ? parseInt(hourPart.value, 10) : date.getUTCHours() + 2;
		} catch (e) {
			return date.getUTCHours() + 2;
		}
	}

	addBusinessDays(date, days) {
		const result = new Date(date);
		let added = 0;
		while (added < days) {
			result.setDate(result.getDate() + 1);
			const day = result.getDay();
			if (day !== 0 && day !== 6) added++;
		}
		return result;
	}

	formatDeliveryDate(date) {
		const locale = this.getLocale();

		// Layer 1: Try native Intl.DateTimeFormat with detected locale
		try {
			return new Intl.DateTimeFormat(locale, {
				weekday: "short",
				month: "short",
				day: "numeric"
			}).format(date);
		} catch (e1) {
			console.warn("AmourLinen: Intl.DateTimeFormat failed for locale " + locale + ", falling back to English Intl", e1);

			// Layer 2: Try with English locale (always supported)
			try {
				return new Intl.DateTimeFormat("en", {
					weekday: "short",
					month: "short",
					day: "numeric"
				}).format(date);
			} catch (e2) {
				console.warn("AmourLinen: English Intl.DateTimeFormat also failed, using hardcoded fallback", e2);

				// Layer 3: Hardcoded English fallback (legacy behavior)
				const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
				const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
				return days[date.getDay()] + " " + months[date.getMonth()] + " " + date.getDate();
			}
		}
	}

	// ============================================================
	// Stock indicators
	// ============================================================

	renderStockIndicators() {
		// Stock indicators removed — clear and hide every slot so nothing renders.
		this.querySelectorAll("[data-stock-slot]").forEach((slot) => {
			slot.textContent = "";
			slot.style.display = "none";
		});
	}

	hashString(str) {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			hash = ((hash << 5) - hash) + str.charCodeAt(i);
			hash |= 0;
		}
		return Math.abs(hash);
	}

	loadUpsell() {
		const upsellWrapper = this.querySelector("[data-curated-upsell]");
		const upsellContainer = this.querySelector("#AlUpsellList");
		if (!upsellWrapper || !upsellContainer) return;

		const cartProductIds = Array.from(
			this.querySelectorAll(".cart-item[data-product-id]")
		).map((el) => String(el.getAttribute("data-product-id")));

		const items = upsellContainer.querySelectorAll(".al-upsell__item[data-product-id]");
		let visibleCount = 0;
		items.forEach((item) => {
			const itemId = String(item.getAttribute("data-product-id"));
			if (cartProductIds.includes(itemId)) {
				item.style.display = "none";
			} else {
				item.style.display = "";
				visibleCount++;
			}
		});

		if (visibleCount === 0) {
			upsellWrapper.style.display = "none";
		} else {
			upsellWrapper.style.display = "";
		}

		upsellContainer.querySelectorAll(".al-upsell__item-add:not([data-al-bound])").forEach((btn) => {
			btn.dataset.alBound = "1";
			btn.addEventListener("click", this.handleUpsellAdd.bind(this));
		});
	}

	handleUpsellAdd(event) {
		const btn = event.currentTarget;
		const variantId = btn.getAttribute("data-variant-id");
		const hasOptions = btn.getAttribute("data-has-options") === "true";
		const itemEl = btn.closest(".al-upsell__item");

		if (hasOptions && itemEl) {
			this.openSizeModal(itemEl);
			return;
		}

		if (!variantId) return;
		this.addVariantToCart(variantId, btn);
	}

	addVariantToCart(variantId, btn) {
		const originalText = btn ? btn.textContent : null;
		if (btn) {
			btn.disabled = true;
			btn.textContent = this.t("button_adding", "Adding...");
		}

		return fetch("/cart/add.js", {
			method: "POST",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
		})
			.then((r) => r.json())
			.then(() => {
				return this.refreshDrawer();
			})
			.catch((err) => {
				console.error("AmourLinen add to cart failed:", err);
				if (btn) {
					btn.disabled = false;
					btn.textContent = originalText || this.t("button_add_default", "Add");
				}
			});
	}

	// ============================================================
	// Size picker modal — sizes only
	// ============================================================
	initSizeModal() {
		const modal = document.getElementById("AlSizeModal");
		if (!modal || modal.dataset.alBound === "1") return;
		modal.dataset.alBound = "1";

		const self = this;

		modal.addEventListener("click", function(e) {
			if (e.target.closest("[data-al-size-close]")) {
				self.closeSizeModal();
			}
		});

		document.addEventListener("keydown", function(e) {
			if (e.key !== "Escape") return;
			if (modal && !modal.hidden) {
				self.closeSizeModal();
			}
		});
	}

	openSizeModal(itemEl) {
		const modal = document.getElementById("AlSizeModal");
		if (!modal || !itemEl) return;

		const title = itemEl.getAttribute("data-product-title") || "";
		const image = itemEl.getAttribute("data-product-image") || "";
		const price = itemEl.getAttribute("data-product-price") || "";
		const wasPrice = itemEl.getAttribute("data-product-was-price") || "";
		const sizePosition = parseInt(itemEl.getAttribute("data-size-position") || "0", 10);

		let variants = [];
		try {
			variants = JSON.parse(itemEl.getAttribute("data-variants") || "[]");
		} catch (e) {
			console.error("AmourLinen size modal: failed to parse variants", e);
			return;
		}

		const titleEl = document.getElementById("AlSizeModalProductTitle");
		const pricesEl = document.getElementById("AlSizeModalPrices");
		const imgEl = document.getElementById("AlSizeModalImg");
		const sizesEl = document.getElementById("AlSizeModalSizes");
		const feedbackEl = document.getElementById("AlSizeModalFeedback");

		if (titleEl) titleEl.textContent = title;
		if (imgEl) {
			if (image) {
				imgEl.src = image;
				imgEl.alt = title;
				imgEl.style.display = "";
			} else {
				imgEl.style.display = "none";
			}
		}
		if (pricesEl) {
			if (wasPrice) {
				pricesEl.innerHTML =
					'<span class="al-size-modal__price al-size-modal__price--sale">' + escapeHtmlSafe(price) + '</span>' +
					'<span class="al-size-modal__price al-size-modal__price--was">' + escapeHtmlSafe(wasPrice) + '</span>';
			} else {
				pricesEl.innerHTML = '<span class="al-size-modal__price">' + escapeHtmlSafe(price) + '</span>';
			}
		}
		if (feedbackEl) {
			feedbackEl.textContent = "";
			feedbackEl.className = "al-size-modal__feedback";
		}

		const sizeKey = sizePosition > 0 ? "option" + sizePosition : null;

		if (sizesEl) {
			sizesEl.innerHTML = variants.map(function(v) {
				let label;
				if (sizeKey && v[sizeKey]) {
					label = v[sizeKey];
				} else {
					label = v.title;
				}

				const disabled = v.available ? "" : "disabled";
				const soldOutClass = v.available ? "" : " is-soldout";
				return (
					'<button type="button" class="al-size-modal__chip al-size-modal__chip--size' + soldOutClass + '" ' +
					'data-variant-id="' + v.id + '" ' + disabled + '>' +
					escapeHtmlSafe(label) +
					'</button>'
				);
			}).join("");

			const self = this;
			sizesEl.querySelectorAll(".al-size-modal__chip--size:not([disabled])").forEach(function(btn) {
				btn.addEventListener("click", function() {
					self.handleSizeSelection(btn);
				});
			});
		}

		modal.hidden = false;
		document.body.style.overflow = "hidden";
	}

	closeSizeModal() {
		const modal = document.getElementById("AlSizeModal");
		if (!modal) return;
		modal.hidden = true;
		document.body.style.overflow = "";
	}

	handleSizeSelection(btn) {
		const variantId = btn.getAttribute("data-variant-id");
		const feedbackEl = document.getElementById("AlSizeModalFeedback");
		if (!variantId) return;

		const originalLabel = btn.textContent;

		const allBtns = document.querySelectorAll("#AlSizeModal .al-size-modal__chip");
		allBtns.forEach(function(b) { b.disabled = true; });
		btn.classList.add("is-loading");
		btn.textContent = this.t("button_adding", "Adding...");

		const self = this;
		fetch("/cart/add.js", {
			method: "POST",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
		})
			.then(function(r) { return r.json(); })
			.then(function(data) {
				if (data && data.status && data.status >= 400) {
					throw new Error(data.description || "Add to cart failed");
				}
				return self.refreshDrawer();
			})
			.then(function() {
				self.closeSizeModal();
			})
			.catch(function(err) {
				console.error("AmourLinen size add failed:", err);
				if (feedbackEl) {
					feedbackEl.textContent = self.t("size_add_failed", "Couldn't add — please try again.");
					feedbackEl.className = "al-size-modal__feedback is-error";
				}
				allBtns.forEach(function(b) {
					if (!b.classList.contains("is-soldout")) b.disabled = false;
				});
				btn.textContent = originalLabel;
				btn.classList.remove("is-loading");
			});
	}

	initPromoCode() {
		const applyBtn = this.querySelector("#AlPromoApply");
		const input = this.querySelector("#AlPromoInput");
		if (!applyBtn || !input) return;

		if (applyBtn.dataset.alBound === "1") return;
		applyBtn.dataset.alBound = "1";

		const submit = () => this.applyPromoCode(input.value.trim());

		applyBtn.addEventListener("click", submit);
		input.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				e.preventDefault();
				submit();
			}
		});
	}

	applyPromoCode(code) {
		const feedback = this.querySelector("#AlPromoFeedback");
		const btn = this.querySelector("#AlPromoApply");
		if (!code) {
			if (feedback) {
				feedback.textContent = this.t("promo_enter_code", "Enter a discount code.");
				feedback.className = "al-promo__feedback is-error";
			}
			return;
		}

		if (btn) {
			btn.disabled = true;
			btn.textContent = this.t("promo_applying", "Applying...");
		}
		if (feedback) {
			feedback.textContent = "";
			feedback.className = "al-promo__feedback";
		}

		fetch("/discount/" + encodeURIComponent(code), {
			method: "GET",
			credentials: "same-origin"
		})
			.then(() => {
				return fetch("/cart/update.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ attributes: { _discount_applied: Date.now() } })
				});
			})
			.then((r) => r.json())
			.then((cart) => {
				const hasDiscount =
					(cart.cart_level_discount_applications &&
						cart.cart_level_discount_applications.length > 0) ||
					cart.total_discount > 0;

				if (hasDiscount) {
					if (feedback) {
						feedback.textContent = this.t("promo_success", "Discount code applied.");
						feedback.className = "al-promo__feedback is-success";
					}
					return this.refreshDrawer();
				} else {
					if (feedback) {
						feedback.textContent = this.t("promo_invalid", "This code isn't valid.");
						feedback.className = "al-promo__feedback is-error";
					}
					if (btn) {
						btn.disabled = false;
						btn.textContent = this.t("button_apply", "Apply");
					}
				}
			})
			.catch((err) => {
				console.error("Promo apply failed:", err);
				if (feedback) {
					feedback.textContent = this.t("promo_error_generic", "Something went wrong. Please try again.");
					feedback.className = "al-promo__feedback is-error";
				}
				if (btn) {
					btn.disabled = false;
					btn.textContent = this.t("button_apply", "Apply");
				}
			});
	}

	refreshDrawer() {
		return fetch(window.location.pathname + "?sections=cart-drawer,cart-icon-bubble")
			.then((r) => r.json())
			.then((sections) => {
				const cartDrawerHtml = sections["cart-drawer"];
				const iconBubbleHtml = sections["cart-icon-bubble"];

				if (cartDrawerHtml) {
					const newInner = new DOMParser()
						.parseFromString(cartDrawerHtml, "text/html")
						.querySelector("#CartDrawer");
					if (newInner) {
						document.querySelector("#CartDrawer").innerHTML = newInner.innerHTML;
					}
				}
				if (iconBubbleHtml) {
					const newBubble = new DOMParser()
						.parseFromString(iconBubbleHtml, "text/html")
						.querySelector("#cart-icon-bubble");
					const currentBubble = document.querySelector("#cart-icon-bubble");
					if (newBubble && currentBubble) {
						currentBubble.innerHTML = newBubble.innerHTML;
					}
				}

				const cartDrawerEl = this.querySelector("#CartDrawer");
				if (cartDrawerEl) {
					cartDrawerEl.classList.remove("notification-open");
				}
				this.querySelectorAll(".cart-item.active").forEach((item) => {
					item.classList.remove("active");
				});

				const overlay = this.querySelector("#CartDrawer-Overlay");
				if (overlay) overlay.addEventListener("click", this.close.bind(this));
				this.rehydrate();
			});
	}

	formatMoney(cents) {
		if (window.Shopify && window.Shopify.formatMoney) {
			return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || "€{{amount}}");
		}
		return "€" + (cents / 100).toFixed(2);
	}

	escapeHtml(str) {
		return escapeHtmlSafe(str);
	}
}

function escapeHtmlSafe(str) {
	return String(str).replace(/[&<>"']/g, function(c) {
		const map = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
		return map[c];
	});
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{ id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
			{ id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" }
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(routes.cart_update_url, {
							...fetchConfig(),
							...{ body }
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}